/**
 * Browser-based web push via Firebase Cloud Messaging.
 *
 * Runs ONLY when the app is loaded in a real web browser (Capacitor native
 * platforms use /native/pushNotifications.js instead).
 *
 * Flow:
 *  1. Lazy-import the firebase SDK (small entry chunk avoided on first load).
 *  2. Register /firebase-messaging-sw.js as the FCM service worker.
 *  3. Request Notification.permission (browser prompt).
 *  4. getToken(messaging, {vapidKey, serviceWorkerRegistration}) → FCM token.
 *  5. POST the token to /api/notifications/register-device.
 *  6. onMessage() pipes foreground pushes back to the caller via onIncoming.
 */
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const VAPID_KEY = process.env.REACT_APP_FIREBASE_VAPID_KEY;

let _registered = false;
let _swRegistration = null;
let _messaging = null;
let _currentToken = null;

export function isWebPushSupported() {
  return (
    typeof window !== 'undefined' &&
    'serviceWorker' in navigator &&
    'Notification' in window &&
    'PushManager' in window &&
    !!VAPID_KEY
  );
}

async function ensureMessaging() {
  if (_messaging) return _messaging;
  const { initializeApp, getApps } = await import('firebase/app');
  const { getMessaging, isSupported } = await import('firebase/messaging');

  const ok = await isSupported().catch(() => false);
  if (!ok) throw new Error('Firebase messaging not supported by this browser');

  const config = {
    apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
    authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
    projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
    storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
    messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
    appId: process.env.REACT_APP_FIREBASE_APP_ID,
  };
  const app = getApps().length ? getApps()[0] : initializeApp(config);
  _messaging = getMessaging(app);
  return _messaging;
}

async function ensureServiceWorker() {
  if (_swRegistration) return _swRegistration;
  _swRegistration = await navigator.serviceWorker.register(
    '/firebase-messaging-sw.js',
    { scope: '/' }
  );
  // Wait until the SW is actually active before requesting an FCM token,
  // otherwise getToken() can race and fail.
  if (_swRegistration.installing || _swRegistration.waiting) {
    await new Promise((resolve) => {
      const sw = _swRegistration.installing || _swRegistration.waiting;
      if (!sw) return resolve();
      sw.addEventListener('statechange', () => {
        if (sw.state === 'activated') resolve();
      });
    });
  }
  return _swRegistration;
}

/**
 * Request permission, get an FCM token, register it with the backend, and
 * start listening for foreground pushes.
 *
 * @param {{onIncoming?: function}} opts
 * @returns {Promise<{ok: boolean, reason?: string, token?: string}>}
 */
export async function enableWebPush({ onIncoming, getAuthToken } = {}) {
  if (!isWebPushSupported()) return { ok: false, reason: 'unsupported' };

  if (Notification.permission === 'denied') {
    return { ok: false, reason: 'denied' };
  }
  if (Notification.permission === 'default') {
    const perm = await Notification.requestPermission();
    if (perm !== 'granted') return { ok: false, reason: 'denied' };
  }

  let messaging;
  let swReg;
  try {
    messaging = await ensureMessaging();
    swReg = await ensureServiceWorker();
  } catch (e) {
    console.warn('Web push setup failed:', e);
    return { ok: false, reason: 'init-failed' };
  }

  const { getToken, onMessage } = await import('firebase/messaging');

  let fcmToken;
  try {
    fcmToken = await getToken(messaging, {
      vapidKey: VAPID_KEY,
      serviceWorkerRegistration: swReg,
    });
  } catch (e) {
    console.warn('FCM getToken failed:', e);
    return { ok: false, reason: 'token-failed' };
  }
  if (!fcmToken) return { ok: false, reason: 'no-token' };

  _currentToken = fcmToken;

  // Register with backend (needs an auth token — JWT lives in localStorage).
  try {
    const authToken = typeof getAuthToken === 'function'
      ? getAuthToken()
      : localStorage.getItem('token');
    await axios.post(
      `${API}/notifications/register-device`,
      { token: fcmToken, platform: 'web' },
      authToken ? { headers: { Authorization: `Bearer ${authToken}` } } : {}
    );
  } catch (e) {
    console.warn('register-device failed:', e?.response?.data || e?.message);
    return { ok: false, reason: 'register-failed', token: fcmToken };
  }

  // Foreground messages — fire the same callback shape as native push for
  // a unified call site in usePushNotifications.
  if (!_registered) {
    onMessage(messaging, (payload) => {
      if (typeof onIncoming === 'function') {
        onIncoming({
          kind: 'received',
          notification: {
            title: payload?.notification?.title,
            body: payload?.notification?.body,
            data: payload?.data,
          },
        });
      }
      // Best-effort "delivered" analytics ping.
      const bid = payload?.data?.broadcast_id;
      if (bid) {
        axios.post(`${API}/notifications/broadcasts/${bid}/track`, { event: 'delivered' })
          .catch(() => {});
      }
    });

    // Tap-from-background — sent by the SW.
    if (navigator.serviceWorker) {
      navigator.serviceWorker.addEventListener('message', (event) => {
        if (event.data?.kind !== 'push-click') return;
        if (typeof onIncoming === 'function') {
          onIncoming({
            kind: 'action',
            action: { notification: { data: { deep_link: event.data.deep_link } } },
          });
        }
      });
    }
    _registered = true;
  }

  return { ok: true, token: fcmToken };
}

/** Best-effort unregister — tells backend to forget this browser's token. */
export async function disableWebPush({ getAuthToken } = {}) {
  if (!_currentToken) return;
  try {
    const authToken = typeof getAuthToken === 'function'
      ? getAuthToken()
      : localStorage.getItem('token');
    await axios.delete(`${API}/notifications/register-device`, {
      data: { token: _currentToken, platform: 'web' },
      headers: authToken ? { Authorization: `Bearer ${authToken}` } : {},
    });
  } catch (e) {
    console.warn('disable web push: deregister failed', e);
  }
  _currentToken = null;
  _registered = false;
}
